import React, { useCallback, useState, useRef } from 'react';
import {
  Paper,
  Text,
  Stack,
  Group,
  Button,
  ActionIcon,
  Image,
  Grid,
  Alert,
  Box,
  List,
} from '@mantine/core';
import {
  IconCamera,
  IconUpload,
  IconX,
  IconLibraryPhoto,
  IconAlertCircle,
  IconInfoCircle,
  IconFileTypePdf,
} from '@tabler/icons-react';
import { Dropzone, FileWithPath, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone';
import '@mantine/dropzone/styles.css';
import { notifications } from '@mantine/notifications';

const ACCEPTED_MIME_TYPES = [...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE];

const isPdf = (file: File) => file.type === 'application/pdf';

interface ImageUploadProps {
  onImagesSelected: (images: File[]) => void;
  onImagesCleared: () => void;
  maxImages?: number;
  maxFileSize?: number; // in bytes
  disabled?: boolean;
  showTips?: boolean;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onImagesSelected,
  onImagesCleared,
  maxImages = 5,
  maxFileSize = 20 * 1024 * 1024, // 20MB
  disabled = false,
  showTips = true,
}) => {
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Generate image previews. PDFs render as a file tile instead, so they
  // get an empty preview.
  const generatePreviews = useCallback((files: File[]) => {
    const previewPromises = files.map((file) => {
      return new Promise<string>((resolve) => {
        if (isPdf(file)) {
          resolve('');
          return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previewPromises).then(setPreviews);
  }, []);

  const handleFilesSelected = useCallback((files: FileWithPath[]) => {
    if (files.length === 0) return;

    // Validate file count
    if (images.length + files.length > maxImages) {
      notifications.show({
        title: 'Too many files',
        message: `Maximum ${maxImages} files allowed. You currently have ${images.length} file(s).`,
        color: 'orange',
        icon: <IconAlertCircle size={18} />,
        autoClose: 7000,
      });
      return;
    }

    // Validate file sizes
    const oversizedFiles = files.filter(file => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      notifications.show({
        title: 'File too large',
        message: `Some files are too large. Maximum size is ${Math.round(maxFileSize / (1024 * 1024))}MB per file.`,
        color: 'orange',
        icon: <IconAlertCircle size={18} />,
        autoClose: 7000,
      });
      return;
    }

    // Add new files to existing ones
    const newImages = [...images, ...files];
    setImages(newImages);
    generatePreviews(newImages);
    onImagesSelected(newImages);
  }, [images, maxImages, maxFileSize, onImagesSelected, generatePreviews]);

  const handleCameraCapture = useCallback(() => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, []);

  const handleCameraInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFilesSelected(Array.from(files) as FileWithPath[]);
    }
  }, [handleFilesSelected]);

  const removeImage = useCallback((index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    
    setImages(newImages);
    setPreviews(newPreviews);
    
    if (newImages.length === 0) {
      onImagesCleared();
    } else {
      onImagesSelected(newImages);
    }
  }, [images, previews, onImagesSelected, onImagesCleared]);

  const clearAllImages = useCallback(() => {
    setImages([]);
    setPreviews([]);
    onImagesCleared();
  }, [onImagesCleared]);

  return (
    <Stack gap="md">
      {/* Upload Area */}
      {images.length < maxImages && (
        <Dropzone
          onDrop={handleFilesSelected}
          accept={ACCEPTED_MIME_TYPES}
          maxSize={maxFileSize}
          disabled={disabled}
          multiple
        >
          <Stack gap="md" align="center" style={{ pointerEvents: 'none' }} mih={200} justify="center">
            <Group justify="center" gap="xl">
              <Dropzone.Accept>
                <IconUpload size={50} stroke={1.5} color="var(--mantine-color-blue-6)" />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={50} stroke={1.5} color="var(--mantine-color-red-6)" />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconLibraryPhoto size={50} stroke={1.5} color="var(--mantine-color-dimmed)" />
              </Dropzone.Idle>

              <div>
                <Text size="xl" inline>
                  Drop recipe pictures, screenshots, or PDFs here
                </Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  Upload up to {maxImages} files (max {Math.round(maxFileSize / (1024 * 1024))}MB each)
                </Text>
              </div>
            </Group>
            
            {/* Take Photo button integrated into dropzone on mobile */}
            <Button
              variant="subtle"
              leftSection={<IconCamera size={16} />}
              onClick={handleCameraCapture}
              disabled={disabled}
              hiddenFrom="md"
              style={{ pointerEvents: 'auto' }}
              size="sm"
              color="gray"
            >
              Take Photo
            </Button>
          </Stack>
        </Dropzone>
      )}

      {/* Camera Input (hidden) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        style={{ display: 'none' }}
        onChange={handleCameraInputChange}
      />


      {/* Image Previews */}
      {images.length > 0 && (
        <Paper p="md" radius="lg" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={500}>
                Selected files ({images.length}/{maxImages})
              </Text>
              <Button
                variant="subtle"
                color="orange"
                size="xs"
                onClick={clearAllImages}
                disabled={disabled}
              >
                Clear all
              </Button>
            </Group>

            <Grid>
              {previews.map((preview, index) => (
                <Grid.Col key={index} span={{ base: 12, sm: 6, md: 4 }}>
                  <Box pos="relative">
                    {isPdf(images[index]) ? (
                      <Stack
                        h={120}
                        gap={4}
                        align="center"
                        justify="center"
                        px="sm"
                        bg="var(--mantine-color-gray-0)"
                        style={{
                          borderRadius: 'var(--mantine-radius-md)',
                          border: '1px solid var(--mantine-color-gray-3)',
                        }}
                      >
                        <IconFileTypePdf size={36} stroke={1.5} color="var(--mantine-color-red-6)" />
                        <Text size="xs" c="dimmed" ta="center" lineClamp={2} style={{ wordBreak: 'break-all' }}>
                          {images[index].name}
                        </Text>
                      </Stack>
                    ) : (
                      <Image
                        src={preview}
                        alt={`Recipe image ${index + 1}`}
                        radius="md"
                        h={120}
                        fit="cover"
                      />
                    )}
                    <ActionIcon
                      pos="absolute"
                      top={5}
                      right={5}
                      variant="filled"
                      color="orange"
                      size="sm"
                      onClick={() => removeImage(index)}
                      disabled={disabled}
                      style={{ zIndex: 1 }}
                    >
                      <IconX size={12} />
                    </ActionIcon>
                    <Box
                      pos="absolute"
                      bottom={5}
                      left={5}
                      bg="rgba(0,0,0,0.6)"
                      px={4}
                      py={2}
                      style={{ borderRadius: 'var(--mantine-radius-xs)' }}
                    >
                      <Text size="xs" c="white">
                        {Math.round(images[index].size / 1024)}KB
                      </Text>
                    </Box>
                  </Box>
                </Grid.Col>
              ))}
            </Grid>

            {/* Tips */}
            {showTips && (
              <Alert
                icon={<IconInfoCircle size={16} />}
                title="Tips for better results"
                color="blue"
                variant="light"
                radius="lg"
              >
                <List
                  spacing="sm"
                  size="sm"
                  center
                >
                  <List.Item>Make sure text is clear and legible</List.Item>
                  <List.Item>Include both ingredients and instructions</List.Item>
                  <List.Item>Multiple images can capture different parts of long recipes</List.Item>
                </List>
              </Alert>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};
